import React from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { Domain } from 'models';
import { usePermissions } from 'models/user/permissions';

import { Button, Icon, Message, Status, WeezHeader, ToastContext } from '@weezevent/nacre';

import { ActiveDomains, AddOrganizationModal, InactiveDomains } from './components';

import { DOMAIN_STATUS } from '../emails.constants';

import css from '../emails.module.css';

export const DomainInformation = ({ domainName }) => {
    const { t } = useTranslation();
    const history = useHistory();
    const permissions = usePermissions();
    const toast = React.useContext(ToastContext);

    const [openModal, setOpenModal] = React.useState(false);
    const [isRefreshing, setIsRefreshing] = React.useState(false);

    const [domain, sync] = Domain.useApiModel(
        {
            id: domainName,
            strict: true,
            cache: false,
            allow_cache: false
        },
        [domainName]
    );

    const handleRefreshStatus = () => {
        setIsRefreshing(true);
        domain
            .refreshStatus()
            .then(() => {
                toast.success(t('sauron.emails.domains.toast.refresh-success', 'Status synchronized with AWS'));
                sync();
            })
            .catch(error => {
                toast.error(t('sauron.emails.domains.toast.refresh-error', 'Error while synchronizing with AWS'));
            })
            .finally(() => {
                setIsRefreshing(false);
            });
    };

    const { type, statusKey, statusColor } = DOMAIN_STATUS[domain?.verification_status] ?? {};

    return (
        domain && (
            <>
                <WeezHeader
                    rightComponent={[
                        <HeaderActions
                            key="header-actions"
                            isAdmin={permissions.isAdmin()}
                            domain={domain}
                            isRefreshing={isRefreshing}
                            onRefresh={handleRefreshStatus}
                            onOpenModal={() => setOpenModal(true)}
                            t={t}
                        />
                    ]}
                    leftComponent={[]}
                    title={
                        <div className={css['header-title-container']}>
                            <div className={css['header-button']}>
                                <Button className={css['header-icon']} icon={<Icon name="at" size="huge" />} onClick={() => history.push('/emails/domains')} />
                            </div>
                            {domain.name}
                            <Status color={statusColor} text={t(`sauron.emails.domains.status.${statusKey}`)} />
                        </div>
                    }
                    backLink={{ onClick: () => history.push('/emails/domains') }}
                />
                {type && (
                    <Message type={type} title={t(`sauron.emails.domains.domain-info.message.title.${statusKey}`)}>
                        {t(`sauron.emails.domains.domain-info.message.description.${statusKey}`)}
                    </Message>
                )}
                {openModal && <AddOrganizationModal open={openModal} onClose={() => setOpenModal(false)} domain={domain} syncDomain={sync} />}
                {domain.is_success ? <ActiveDomains domain={domain} syncDomain={sync} /> : <InactiveDomains domain={domain} />}
            </>
        )
    );
};

const HeaderActions = ({ isAdmin, domain, isRefreshing, onRefresh, onOpenModal, t }) => {
    if (!isAdmin || !domain) return null;

    return (
        <>
            <Button
                key="refresh-aws"
                secondary
                loading={isRefreshing}
                onClick={onRefresh}
                icon={<Icon name="sync" />}
                label={t('sauron.emails.domains.refresh-status', 'Check on AWS')}
            />

            {domain.is_success && <Button key="add-org" primary onClick={onOpenModal} icon={<Icon name="plus" />} label={t('sauron.emails.domains.add-organization')} />}
        </>
    );
};
